import React, { useState, useEffect } from 'react';
import { TimeEntry, Activity, Customer, Project } from '../types/TimeEntry';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import { useAuth } from '../contexts/AuthContext';
import './DayDetailModal.css';

interface DayDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  entries: TimeEntry[];
  onEntriesUpdate: (entries: TimeEntry[]) => void;
  currentUser: { Id: number; Title: string; EMail: string; LoginName: string } | null;
}

interface EditingEntry {
  id: number;
  date: string;
  hours: string;
  activityId: string;
  customerId: string;
  projectId: string;
  title: string;
  details: string;
}

const DayDetailModal: React.FC<DayDetailModalProps> = ({
  isOpen,
  onClose,
  date,
  entries,
  onEntriesUpdate,
  currentUser
}) => {
  const [editingEntry, setEditingEntry] = useState<EditingEntry | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const { getAccessToken } = useAuth();

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  // Load activities and customers when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setConfirmDeleteId(null);
      loadLookupData();
      
      // Auto-edit if clicking on a single entry from calendar
      if (entries.length === 1) {
        const entry = entries[0];
        setEditingEntry({
          id: entry.Id,
          date: entry.Date,
          hours: entry.Hours.toString(),
          activityId: entry.Activity.Id.toString(),
          customerId: entry.Customer.Id.toString(),
          projectId: entry.Project?.Id.toString() || '',
          title: entry.Title || '',
          details: entry.Details || ''
        });
        // Load activities for the selected customer
        const customerId = entry.Customer.Id;
        if (customerId) {
          loadActivitiesForCustomer(customerId, entry.Project?.Id);
        }
      } else {
        setEditingEntry(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entries]);

  const loadLookupData = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Failed to get access token');
        return;
      }

      // currentUser is fetched once in TimeRegistrationApp - reusing it here (instead of a fresh
      // getCurrentUserInfo call) lets the allowed-projects lookup run in the same parallel batch below.
      const currentUserData = currentUser || await timeRegistrationService.getCurrentUserInfo(accessToken);

      const [activitiesData, customersData, projectsData, allowedProjectIds] = await Promise.all([
        timeRegistrationService.getActivities(accessToken),
        timeRegistrationService.getCustomers(accessToken),
        timeRegistrationService.getProjects(accessToken),
        timeRegistrationService.getAllowedProjectIdsForUser(currentUserData.Id, accessToken)
      ]);

      setActivities(activitiesData.filter(a => a.IsActive !== false));
      const activeCustomers = customersData.filter(c => c.IsActive !== false);
      setCustomers(activeCustomers);

      const activeProjects = projectsData.filter(p => p.IsActive !== false);
      // External users listed in /Lists/ExternalUsersProjects only see the projects assigned to them there.
      setProjects(allowedProjectIds.length > 0
        ? activeProjects.filter(p => allowedProjectIds.includes(p.Id))
        : activeProjects);
    } catch (err) {
      console.error('Error loading lookup data:', err);
      setError('Kunde inte ladda aktiviteter, kunder och projekt');
    } finally {
    }
  };

  const loadActivitiesForCustomer = async (customerId: number, projectId?: number) => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }
      const activitiesForCustomer = await timeRegistrationService.getActivitiesForCustomer(customerId, accessToken, projectId);
      setFilteredActivities(activitiesForCustomer.filter(a => a.IsActive !== false));
    } catch (err) {
      console.error('Error loading activities for customer:', err);
      setFilteredActivities([]);
    }
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDeleteEntry = async (entryId: number) => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Failed to get access token');
        return;
      }

      await timeRegistrationService.deleteTimeEntry(entryId, accessToken);
      const updatedEntries = entries.filter(entry => entry.Id !== entryId);
      onEntriesUpdate(updatedEntries);
      onClose();

    } catch (err) {
      console.error('Error deleting entry:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte ta bort tidsregistrering');
    } finally {
      setLoading(false);
      setConfirmDeleteId(null);
    }
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setEditingEntry({
      id: entry.Id,
      date: entry.Date,
      hours: entry.Hours.toString(),
      activityId: entry.Activity.Id.toString(),
      customerId: entry.Customer.Id.toString(),
      projectId: entry.Project?.Id.toString() || '',
      title: entry.Title || '',
      details: entry.Details || ''
    });
    // Load activities for the selected customer
    const customerId = entry.Customer.Id;
    if (customerId) {
      loadActivitiesForCustomer(customerId, entry.Project?.Id);
    }
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
  };

  const incrementHours = (step: number) => {
    if (!editingEntry) return;
    const currentHours = parseFloat(editingEntry.hours) || 0;
    const newHours = Math.max(1, Math.min(24, currentHours + step));
    setEditingEntry({...editingEntry, hours: newHours.toString()});
  };

  const handleCustomerChange = (value: string) => {
    setEditingEntry(prev => prev ? { ...prev, customerId: value, projectId: '', activityId: '' } : null);
    // Load activities for the selected customer
    if (value) {
      const customerId = parseInt(value);
      loadActivitiesForCustomer(customerId);
    } else {
      setFilteredActivities([]);
    }
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    const hours = parseFloat(editingEntry.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setError('Vänligen ange giltiga timmar (0,1 - 24)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      const selectedActivity = activities.find(a => a.Id.toString() === editingEntry.activityId);
      const selectedCustomer = customers.find(c => c.Id.toString() === editingEntry.customerId);
      const selectedProject = editingEntry.projectId ? projects.find(p => p.Id.toString() === editingEntry.projectId) : null;

      if (!selectedActivity || !selectedCustomer) {
        setError('Vald aktivitet eller kund hittades inte');
        return;
      }

      const entryToUpdate = entries.find(e => e.Id === editingEntry.id);
      if (!entryToUpdate) {
        setError('Registrering hittades inte');
        return;
      }

      const updatedEntry: TimeEntry = {
        ...entryToUpdate,
        Date: editingEntry.date,
        Hours: hours,
        Activity: selectedActivity,
        Customer: selectedCustomer,
        Person: currentUser || entryToUpdate.Person,
        Title: editingEntry.title || undefined,
        Details: editingEntry.details || undefined,
        ...(selectedProject && { Project: { Id: selectedProject.Id, Title: selectedProject.Title } })
      };

      await timeRegistrationService.updateTimeEntry(editingEntry.id, updatedEntry, accessToken);
      const updatedEntries = entries.map(e => e.Id === editingEntry.id ? updatedEntry : e);
      onEntriesUpdate(updatedEntries);
      setEditingEntry(null);
      onClose();

    } catch (err) {
      console.error('Error updating entry:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte uppdatera tidsregistrering');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tidsregistreringar för {formatDate(date)}</h2>
          <button className="close-button" onClick={onClose} aria-label="Stäng">×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message\">
              {error}
            </div>
          )}

          {/* Existing Entries */}
          <div className="existing-entries">
            {entries.length === 0 ? (
              <p className="no-entries">Inga tidsregistreringar för denna dag</p>
            ) : (
              <div className="entries-list">
                {entries.map(entry => (
                  <div key={entry.Id} className="entry-item">
                    {editingEntry?.id === entry.Id ? (
                      <div className="entry-edit-mode">
                        <div className="edit-field edit-field-hours">
                          <label>Timmar</label>
                          <div className="hours-control">
                            <button
                              type="button"
                              className="hours-btn"
                              onClick={() => incrementHours(-1)}
                              disabled={loading}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              max="24"
                              value={editingEntry.hours}
                              onChange={(e) => setEditingEntry({...editingEntry, hours: e.target.value})}
                              disabled={loading}
                              className="hours-input"
                            />
                            <button
                              type="button"
                              className="hours-btn"
                              onClick={() => incrementHours(1)}
                              disabled={loading}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        
                        <div className="edit-field">
                          <label>Datum</label>
                          <input
                            type="date"
                            value={editingEntry.date}
                            onChange={(e) => setEditingEntry({...editingEntry, date: e.target.value})}
                            disabled={loading}
                          />
                        </div>

                        <div className="edit-field">
                          <label>Aktivitet</label>
                          <select
                            value={editingEntry.activityId}
                            onChange={(e) => setEditingEntry({...editingEntry, activityId: e.target.value})}
                            disabled={loading || !editingEntry.customerId}
                          >
                            <option value="">{editingEntry.customerId ? 'Välj aktivitet...' : 'Välj först en kund...'}</option>
                            {filteredActivities.map(activity => (
                              <option key={activity.Id} value={activity.Id.toString()}>
                                {activity.Title}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="edit-field">
                          <label>Kund</label>
                          <select
                            value={editingEntry.customerId}
                            onChange={(e) => handleCustomerChange(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">Välj kund...</option>
                            {customers.map(customer => (
                              <option key={customer.Id} value={customer.Id.toString()}>
                                {customer.Title}
                              </option>
                            ))}
                          </select>
                        </div>

                        {editingEntry.customerId && projects.some(p => p.Customer?.Id.toString() === editingEntry.customerId) && (
                          <div className="edit-field">
                            <label>Projekt</label>
                            <select
                              value={editingEntry.projectId}
                              onChange={(e) => {
                                const selectedProjectId = e.target.value;
                                setEditingEntry({ ...editingEntry, projectId: selectedProjectId, activityId: '' });
                                const customerId = parseInt(editingEntry.customerId);
                                if (selectedProjectId) {
                                  loadActivitiesForCustomer(customerId, parseInt(selectedProjectId));
                                } else {
                                  loadActivitiesForCustomer(customerId);
                                }
                              }}
                              disabled={loading}
                            >
                              <option value="">Välj projekt...</option>
                              {projects
                                .filter(p => p.Customer?.Id.toString() === editingEntry.customerId)
                                .map(project => (
                                  <option key={project.Id} value={project.Id.toString()}>
                                    {project.Title}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                        
                        <div className="edit-field">
                          <label>Beskrivning</label>
                          <input
                            type="text"
                            value={editingEntry.title}
                            onChange={(e) => setEditingEntry({...editingEntry, title: e.target.value})}
                            disabled={loading}
                            placeholder="Kort beskrivning av arbetet"
                          />
                        </div>
                        
                        <div className="edit-field">
                          <label>Längre kommentar/kom ihåg</label>
                          <textarea
                            value={editingEntry.details}
                            onChange={(e) => setEditingEntry({...editingEntry, details: e.target.value})}
                            disabled={loading}
                            placeholder="Detaljerad beskrivning eller saker att komma ihåg"
                            rows={3}
                          />
                        </div>
                        
                        <div className="edit-actions">
                          <button
                            onClick={handleUpdateEntry}
                            className="save-button"
                            disabled={loading}
                          >
                            {loading ? 'Sparar...' : 'Spara'}
                          </button>
                          {confirmDeleteId === editingEntry.id ? (
                            <>
                              <button
                                onClick={() => handleDeleteEntry(editingEntry.id)}
                                className="delete-button-edit confirm"
                                disabled={loading}
                              >
                                {loading ? 'Tar bort...' : 'Säker? Ta bort'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="cancel-button"
                                disabled={loading}
                              >
                                Avbryt
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(editingEntry.id)}
                              className="delete-button-edit"
                              disabled={loading}
                              title="Ta bort"
                            >
                              Ta bort
                            </button>
                          )}
                          <button
                            onClick={handleCancelEdit}
                            className="cancel-button"
                            disabled={loading}
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="entry-display">
                        <button
                          className="entry-content"
                          onClick={() => handleEditEntry(entry)}
                          disabled={loading}
                        >
                          {entry.Customer.Title} - {entry.Activity.Title} ({entry.Hours}h)
                        </button>
                        {confirmDeleteId === entry.Id ? (
                          <>
                            <button
                              onClick={() => handleDeleteEntry(entry.Id)}
                              className="delete-button confirm"
                              disabled={loading}
                              title="Bekräfta borttagning"
                            >
                              Ta bort?
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="delete-button-cancel"
                              disabled={loading}
                              title="Avbryt"
                            >
                              Avbryt
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(entry.Id)}
                            className="delete-button"
                            disabled={loading}
                            title="Ta bort"
                            aria-label="Ta bort tidspost"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayDetailModal;